<script setup lang="ts">
import { computed } from 'vue'
import MaintenanceUpdatedDialog from '~/components/app/MaintenanceUpdatedDialog.vue'
import { isAutomaticModalVisible } from '~/onboarding/automaticModalGate'
import { useOnboardingStore } from '~~/stores/onboarding'
import SourceToastProvider from '~/components/ui/SourceToastProvider.vue'
import { useDeploymentMaintenance } from '~/composables/api/useDeploymentMaintenance'
import { useSeasonalTheme } from '~/composables/ui/useSeasonalTheme'
import { useRecetteDocumentTitle } from '~/composables/layout/useAppEnv'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const DeploymentMaintenanceBanner = defineAsyncWithSkeleton(
  function loadDeploymentMaintenanceBanner() {
    return import('~/components/app/DeploymentMaintenanceBanner.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

useRecetteDocumentTitle()
useHead({
  htmlAttrs: {
    lang: 'fr',
  },
})

const {
  showSeasonalCursor,
  cursorEmoji,
  cursorLeftPx,
  cursorTopPx,
} = useSeasonalTheme()

const { state: maintenanceState, shouldShowUpdatedModal, dismissUpdatedModal } =
  useDeploymentMaintenance()
const onboardingStore = useOnboardingStore()
const showMaintenanceUpdatedModal = computed(() =>
  isAutomaticModalVisible(shouldShowUpdatedModal.value, onboardingStore.isTourActive),
)

function onMaintenanceUpdatedOpen(nextOpen: boolean): void {
  if (onboardingStore.isTourActive) {
    return
  }
  shouldShowUpdatedModal.value = nextOpen
}

const route = useRoute()
const showGlobalMaintenanceBanner = computed(() => !route.path.startsWith('/dev'))
</script>

<template>
  <div class="app-root">
    <SourceToastProvider>
      <DeploymentMaintenanceBanner v-if="showGlobalMaintenanceBanner" :state="maintenanceState" />
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </SourceToastProvider>
    <MaintenanceUpdatedDialog
      :open="showMaintenanceUpdatedModal"
      @update:open="onMaintenanceUpdatedOpen"
      @dismiss="dismissUpdatedModal()"
    />
    <ClientOnly>
      <Transition name="seasonal-cursor">
        <div
          v-if="showSeasonalCursor"
          id="seasonal-cursor"
          :style="{ left: cursorLeftPx, top: cursorTopPx }"
        >
          {{ cursorEmoji }}
        </div>
      </Transition>
      <template #fallback />
    </ClientOnly>
  </div>
</template>

<style scoped>
.app-root {
  display: contents;
}
</style>
